import { useEffect, useRef } from "react";
import { configure } from "safe-stable-stringify";

import type {
    AsidePanelConfig,
    AsidePanelKey,
    MainPanelConfig,
    MainPanelKey,
    TopbarConfig,
} from "../store/panels.store.types";

/**
 * Panel Configuration Sync Hooks
 *
 * These hooks provide a way to sync panel configuration with the store while avoiding
 * unnecessary updates when the configuration hasn't actually changed. They use deep
 * equality comparison via safe-stable-stringify to determine if the config has changed.
 *
 */

const safeStringify = configure({
    deterministic: false,
    maximumDepth: 10,
    strict: false,
});

export function useAsidePanelConfigSync(
    config: Partial<AsidePanelConfig> | undefined,
    panelId: AsidePanelKey,
    setter: (panelId: AsidePanelKey, config: Partial<AsidePanelConfig>) => void,
) {
    const previousConfigRef = useRef<string | null>(null);

    useEffect(() => {
        if (!config) return;

        const currentConfigString = safeStringify(config);

        // Only update if the config has actually changed
        if (previousConfigRef.current !== currentConfigString) {
            previousConfigRef.current = currentConfigString;
            setter(panelId, config);
        }
    }, [config, panelId, setter]);
}

export function useMainPanelConfigSync(
    config: Partial<MainPanelConfig> | undefined,
    panelId: MainPanelKey,
    setter: (panelId: MainPanelKey, config: Partial<MainPanelConfig>) => void,
) {
    const previousConfigRef = useRef<string | null>(null);

    useEffect(() => {
        if (!config) return;

        const currentConfigString = safeStringify(config);

        // Only update if the config has actually changed
        if (previousConfigRef.current !== currentConfigString) {
            previousConfigRef.current = currentConfigString;
            setter(panelId, config);
        }
    }, [config, panelId, setter]);
}

export function useTopbarConfigSync(
    config: Partial<TopbarConfig> | undefined,
    setter: (config: Partial<TopbarConfig>) => void,
) {
    const previousConfigRef = useRef<string | null>(null);

    useEffect(() => {
        if (!config) return;

        const currentConfigString = safeStringify(config);

        // Only update if the config has actually changed
        if (previousConfigRef.current !== currentConfigString) {
            previousConfigRef.current = currentConfigString;
            setter(config);
        }
    }, [config, setter]);
}
